/**
 * User Storage Domain
 * Persists the user profile as a single `user_profile` value, then derives the live current
 * user by overlaying token/auth identity on top of that profile.
 * @module @services/storage
 */

import type { User } from '@shared/types';
import { getAuthState } from './auth-storage';
import { createStorageConfig, STORAGE_KEYS } from './storage-config';
import { getUserInfoFromToken, getFusionId, getUserPublicId } from '@infra/auth';

/**
 * Two storage buckets model the current user: the JWT (`token`) carries identity/auth claims
 * (id, email, role), and this `user_profile` value carries everything else — the DB-only fields
 * (gender, level, timezone, avatar…) that aren't in the token. We persist the whole profile here
 * once at login / after a save and read it back as ONE value, instead of caching individual
 * DB-only fields (e.g. gender) separately.
 */
const { key: USER_PROFILE_KEY, adapter } = createStorageConfig(STORAGE_KEYS.USER_PROFILE);

/** Persist the user profile (call at login and after a profile update). */
export function setUserProfile(user: User): void {
  adapter.setItem<User>(USER_PROFILE_KEY, user);
}

/** Raw persisted profile, or undefined if none was stored this session. */
export function getUserProfile(): User | undefined {
  return adapter.getItem<User | undefined>(USER_PROFILE_KEY, undefined);
}

/**
 * Get current user, or undefined when not authenticated.
 *
 * Token + auth state stay the source of truth for identity fields (id, email, role, name),
 * so they're always fresh and can't go stale against the JWT; the persisted `user_profile`
 * supplies the DB-only rest (gender, level, timezone…). Gating on auth state means a stale
 * profile is never returned after logout / token loss.
 */
export function getCurrentUser(): User | undefined {
  const authState = getAuthState();
  if (!authState.isAuthenticated || !authState.role) return undefined;
  // Public id comes straight from the token (single source of truth), not a stored copy.
  const userPublicId = getUserPublicId();
  if (!userPublicId) return undefined;
  const userInfo = getUserInfoFromToken();
  if (!userInfo?.email) return undefined;
  // Only trust the profile if it belongs to the authenticated user — parity with the old
  // per-uuid gender cache, guarding the rare window where a prior user's profile outlives a
  // token swap that skipped logout. userPublicId === the DB user uuid === profile.uuid.
  const raw = getUserProfile();
  const profile = raw?.uuid === userPublicId ? raw : undefined;
  return {
    ...profile,
    id: userPublicId,
    uuid: userPublicId,
    fusionauthUserId: getFusionId() ?? undefined,
    name: userInfo.name || userInfo.firstName || userInfo.email,
    email: userInfo.email,
    role: authState.role,
  };
}
